import Thread from './assistant-ui/thread';
import { MastraRuntimeProvider, ChatProps, useMastraRuntime } from '../providers/mastra-runtime-provider';
import { ToolFallback } from './assistant-ui/tool-fallback';
import { AllToolsUI } from './tools/all-tools-ui';
import { ArrowRightIcon, CodeIcon, SparklesIcon, SquarePen, Users, Search, ArrowLeft, ArrowRight } from 'lucide-react';
import { useAuth } from '../../../providers/authentication-provider';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@shadcn/ui/components/ui/dropdown-menu';
import { Button } from '@shadcn/ui/components/ui/button';
import { AndsendModel } from '@andsend/services-agent-shared';
import { ThreadHistoryDropdownButton } from './thread-history-dropdown-button';
import { StorageThreadType } from '@mastra/core';
import { useNavigate } from 'react-router-dom';
import { useThread } from '@assistant-ui/react';
import { cn } from '@shadcn/ui/lib/utils';
import { paths } from '../../../../routes/paths';
import { useState } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shadcn/ui/components/ui/tooltip';
import BetaBadge from '../../../components/beta-badge';
import { MarkdownText } from './assistant-ui/markdown-text';
import { v4 as uuid } from '@lukeed/uuid';

export const AgentChat = ({
    agentId,
    agentName,
    threadId,
    initialMessages,
    mastra,
    memory,
    baseUrl,
    refreshThreadList,
    availableAgents,
    setAgentId,
    selectedModel,
    setSelectedModel,
    threads,
    isLoading,
}: ChatProps) => {
    // const { modelSettings, chatWithGenerate } = useContext(AgentContext);

    return (
        <div className="flex flex-col gap-2 w-full h-full justify-center">
            <MastraRuntimeProvider
                agentId={agentId}
                agentName={agentName}
                threadId={threadId}
                initialMessages={initialMessages}
                memory={memory}
                baseUrl={baseUrl}
                mastra={mastra}
                refreshThreadList={refreshThreadList}
                modelSettings={{ maxSteps: 10, selectedModel, temperature: 0.4, topP: 0.9 }}
                // modelSettings={modelSettings}
                // chatWithGenerate={chatWithGenerate}
            >
                <HeaderActions
                    selectedModel={selectedModel}
                    setSelectedModel={setSelectedModel}
                    availableAgents={availableAgents}
                    setAgentId={setAgentId}
                    agentId={agentId}
                    threadId={threadId}
                    threads={threads}
                    isLoading={isLoading}
                />
                <Thread
                    memory={memory}
                    welcome={{
                        message: "Hi, I'm your Customer Relationship Agent",
                        suggestions: [
                            {
                                prompt: 'Show me 5 high-priority actions I can take right now',
                                text: (
                                    <SuggestionRow
                                        icon={
                                            <SparklesIcon className="w-8 h-8 bg-violet-100 dark:bg-violet-900 rounded-full p-[9px] text-violet-600 dark:text-violet-300" />
                                        }
                                        title="Show me 5 important actions"
                                        hoverGradient="from-violet-50 to-fuchsia-50 dark:from-violet-900 dark:to-fuchsia-900"
                                        arrowHoverColor="group-hover:text-violet-500 dark:group-hover:text-violet-300"
                                    />
                                ),
                            },
                            {
                                prompt: 'Show me conversations with people that have written to me in the past 14 days that I have not replied to',
                                text: (
                                    <SuggestionRow
                                        icon={
                                            <Users className="w-8 h-8 bg-rose-100 dark:bg-rose-900 rounded-full p-[9px] text-rose-600 dark:text-rose-300" />
                                        }
                                        title="People I haven't replied to"
                                        hoverGradient="from-rose-50 to-orange-50 dark:from-rose-900 dark:to-orange-900"
                                        arrowHoverColor="group-hover:text-rose-500 dark:group-hover:text-rose-300"
                                    />
                                ),
                            },
                            {
                                prompt: 'Show me recommendations of people I am already connected with that I could reach out to',
                                text: (
                                    <SuggestionRow
                                        icon={
                                            <Search className="w-8 h-8 bg-sky-100 dark:bg-sky-900 rounded-full p-[9px] text-sky-600 dark:text-sky-300" />
                                        }
                                        title="Hidden opportunities in my network"
                                        hoverGradient="from-cyan-50 to-sky-50 dark:from-cyan-900 dark:to-sky-900"
                                        arrowHoverColor="group-hover:text-sky-500 dark:group-hover:text-sky-300"
                                    />
                                ),
                            },
                        ],
                    }}
                    assistantMessage={{
                        components: {
                            ToolFallback: ToolFallback,
                            Text: MarkdownText,
                        },
                    }}
                    assistantAvatar={{
                        src: '/images/logos/andsend-icon-striped-color.png',
                        fallback: 'A',
                    }}
                    composer={{
                        allowAttachments: false,
                    }}
                />
                {/* Register all tool UIs with a single component */}
                <AllToolsUI />
            </MastraRuntimeProvider>
        </div>
    );
};

const SuggestionRow = ({
    title,
    icon,
    className,
    hoverGradient,
    arrowHoverColor,
}: {
    title: string;
    icon: React.ReactNode;
    className?: string;
    hoverGradient?: string; // e.g. 'from-violet-50 to-fuchsia-50'
    arrowHoverColor?: string; // e.g. 'group-hover:text-violet-500'
}) => {
    return (
        <div
            className={cn(
                'group relative overflow-hidden rounded-2xl border border-b-tertiary hover:border-transparent hover:shadow-xs transition-all duration-300 w-full',
                className
            )}
        >
            {/* Gradient overlay for hover */}
            <div
                className={cn(
                    'absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none',
                    hoverGradient
                        ? `bg-linear-to-r ${hoverGradient}`
                        : 'bg-linear-to-r from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800'
                )}
            />
            <button
                type="button"
                className="w-full h-auto flex items-center justify-between py-4 px-4 rounded-xl text-gray-700 dark:text-gray-200 font-normal text-sm bg-transparent hover:bg-transparent relative z-10"
            >
                <div className="flex items-center gap-3">
                    {icon}
                    <span className="text-left">{title}</span>
                </div>
                <ArrowRightIcon
                    className={cn(
                        'w-4 h-4 text-gray-400 dark:text-gray-300 transition-colors duration-300',
                        arrowHoverColor
                    )}
                />
            </button>
        </div>
    );
};

const HeaderActions = ({
    selectedModel,
    setSelectedModel,
    agentId,
    availableAgents,
    setAgentId,
    threadId,
    threads,
    isLoading,
}: {
    selectedModel: AndsendModel;
    setSelectedModel: (model: AndsendModel) => void;
    agentId: string;
    availableAgents: string[];
    setAgentId: (agentId: string) => void;
    threadId: string;
    threads: StorageThreadType[];
    isLoading: boolean;
}) => {
    const {
        authState: { isGlobalAdmin },
    } = useAuth();
    const showDevTools = import.meta.env.MODE === 'development' || isGlobalAdmin;
    const showCost = import.meta.env.MODE === 'development'; // DO NOT show in prod, even for admins
    const [devToolsOpen, setDevToolsOpen] = useState(false);

    const navigate = useNavigate();
    const { threadUsage } = useMastraRuntime();

    // const INPUT_COST_PER_MILLION_TOKENS = 3; // Claude 3.7 Sonnet
    // const OUTPUT_COST_PER_MILLION_TOKENS = 15; // Claude 3.7 Sonnet
    const INPUT_COST_PER_MILLION_TOKENS = 0.8; // Claude 3.5 Haiku
    const OUTPUT_COST_PER_MILLION_TOKENS = 4; // Claude 3.5 Haiku

    const inputCost = ((threadUsage[threadId]?.promptTokens || 0) / 1_000_000) * INPUT_COST_PER_MILLION_TOKENS;
    const outputCost = ((threadUsage[threadId]?.completionTokens || 0) / 1_000_000) * OUTPUT_COST_PER_MILLION_TOKENS;
    const totalCost = inputCost + outputCost;

    const thread = useThread();
    let shouldShowNewChatButton = thread?.messages?.length > 0;
    return (
        <div className="flex flex-row sticky h-0 top-4 right-8 left-8 z-50 gap-2 justify-between">
            <div className="flex flex-col gap-2 ">
                <div className="flex flex-row gap-2 items-center">
                    <ThreadHistoryDropdownButton
                        agentId={agentId}
                        threadId={threadId}
                        threads={threads}
                        isLoading={isLoading}
                    />
                    <Button
                        variant="outline"
                        className={cn(
                            'shadow-md overflow-hidden transition-all duration-100',
                            shouldShowNewChatButton ? '' : 'opacity-0 pointer-events-none'
                        )}
                        onClick={() => navigate(paths.NEW.HOME + '/' + uuid())}
                        size="sm"
                    >
                        <SquarePen />
                        <span>New chat</span>
                    </Button>
                </div>
            </div>
            <div>
                <div className="flex justify-end mb-2 mt-2">
                    <BetaBadge featureName="Agent Chat" />
                </div>
                {showDevTools && (
                    <div
                        className={cn(
                            'flex flex-col gap-4 transition-all duration-300',
                            devToolsOpen ? '' : 'translate-x-[108%]'
                        )}
                    >
                        <Tooltip delayDuration={500}>
                            <TooltipTrigger asChild>
                                <div
                                    className="relative -translate-x-full inset-0 bg-background-secondary rounded-full min-h-3 max-w-3 min-w-3 shadow-xs cursor-pointer"
                                    onClick={() => setDevToolsOpen(!devToolsOpen)}
                                >
                                    {devToolsOpen ? (
                                        <ArrowRight className="w-3 h-3 text-muted-foreground" />
                                    ) : (
                                        <ArrowLeft className="w-3 h-3 text-muted-foreground" />
                                    )}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Toggle dev tools</p>
                            </TooltipContent>
                        </Tooltip>
                        <div className={'flex flex-row gap-2 justify-end'}>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="font-mono shadow-md">
                                        <CodeIcon className="w-4 h-4 mr-1" />
                                        {agentId}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                    {availableAgents.map((model) => (
                                        <DropdownMenuItem key={model} onClick={() => setAgentId(model)}>
                                            {model}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                            {/* <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="shadow-md" disabled>
                            <SparklesIcon className="w-4 h-4" />
                            {Object.entries(AndsendModel)
                                .find(([key, model]) => model === selectedModel)?.[0]
                                ?.toLocaleLowerCase()}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                        {Object.entries(AndsendModel).map(([key, model]) => (
                            <DropdownMenuItem key={key} onClick={() => setSelectedModel(model)}>
                                <SparklesIcon className="w-4 h-4" />
                                {key?.toLocaleLowerCase()}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu> */}
                        </div>
                        {showCost && (
                            <div className="flex flex-col gap-2 font-mono">
                                <span className="text-xs text-muted-foreground mb-2">Thread Usage (this session)</span>
                                <div className="grid grid-cols-[auto_1fr] gap-2 text-xs text-muted-foreground">
                                    <span>Input Tokens:</span>
                                    <span>{threadUsage[threadId]?.promptTokens || 0}</span>
                                    <span>Output Tokens:</span>
                                    <span>{threadUsage[threadId]?.completionTokens || 0}</span>
                                    <span>Input Cost:</span>
                                    <span>${inputCost.toFixed(3)}</span>
                                    <span>Output Cost:</span>
                                    <span>${outputCost.toFixed(3)}</span>
                                    <span className="font-bold">Total Cost:</span>
                                    <span className="font-bold">${totalCost.toFixed(3)}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
